import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ModalController } from '@ionic/angular/standalone';
import { safeModalDismiss } from 'src/app/core/services/modal/modal.utils';
import { gameIdToName } from 'src/app/shared/models/game/game-id.utils';
import { PaymentDrawerStyles } from '../../payments/components/payment-drawer-styles';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { ButtonComponent } from '../../../ui/buttons/button/button.component';
import { CountdownPillComponent } from '../../../ui/pills/countdown-pill/countdown-pill.component';
import { GameTitleComponent } from '../../../ui/game-identity/game-title/game-title.component';
import { GameIconComponent } from '../../../ui/game-identity/game-icon/game-icon.component';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';
import { NgStyle, NgClass } from '@angular/common';
import { createUnfinishedCountdown } from 'src/app/core/services/rooms-results/unfinished-countdown';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';

@Component({
  selector: 'app-continue-game-drawer',
  templateUrl: './continue-game-drawer.component.html',
  styleUrls: ['./continue-game-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    NgClass,
    NavButtonComponent,
    GameIconComponent,
    GameTitleComponent,
    CountdownPillComponent,
    ButtonComponent,
    ThemePipe,
  ],
})
export class ContinueGameDrawerComponent {
  private roomsResultsService = inject(RoomsResultsService);
  private translocoService = inject(TranslocoService);
  private modalController = inject(ModalController);
  private analyticsService = inject(AnalyticsService);

  themeService = inject(ThemeService);
  drawerConfig = this.themeService.payDrawerConfig();

  PaymentDrawerStyles = PaymentDrawerStyles;

  readonly gameId = computed(() => this.roomsResultsService.unfinishedRoom()?.gameId());

  readonly gameName = computed(() => {
    const gameId = this.roomsResultsService.unfinishedRoom()?.gameId();
    return gameId ? gameIdToName(gameId, this.translocoService) : '';
  });

  readonly unfinishedTimeRemaining = createUnfinishedCountdown(
    this.roomsResultsService,
    this.translocoService,
  );

  readonly continueButtonLabel = computed(() =>
    this.translocoService.translate(`components.loading-game-controls.continue-game`),
  );
  readonly unfinishedGamelabel = computed(() =>
    this.translocoService.translate(`components.loading-game-controls.continue-message`),
  );

  readonly drawerStyles: [string, string][] = [
    ['background', 'panel-wager-tier-list'],
    ['border-radius-top', 'panel-wager-tier-list'],
  ];

  readonly titleStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-title'],
    ['text-color', 'panel-wager-tier-list-title'],
  ];

  readonly bodyStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-body'],
    ['text-color', 'panel-wager-tier-list-body'],
  ];

  private userDismissed = signal(false);

  get glowClass(): string {
    return this.themeService.payDrawerConfig().gradient === 'circle'
      ? 'glow-circle'
      : 'glow-linear';
  }

  constructor() {
    // Watch for room timeout — dismiss modal when unfinished room disappears.
    // Skip if the user explicitly dismissed/continued (userDismissed guard),
    // and skip the initial run where hasUnfinishedRoom may already be false.
    let initialized = false;
    effect(async () => {
      const hasRoom = this.roomsResultsService.hasUnfinishedRoom();
      if (!initialized) {
        initialized = true;
        return;
      }
      if (!hasRoom && !this.userDismissed()) {
        this.analyticsService.log(EventName.ContinueGamePopupTimeout);
        await safeModalDismiss(this.modalController);
      }
    });
  }

  public async onContinueClick(): Promise<void> {
    this.userDismissed.set(true);
    await safeModalDismiss(this.modalController);
    if (this.roomsResultsService.hasUnfinishedRoom()) {
      await this.roomsResultsService.launchUnfinishedGame();
      this.analyticsService.log(EventName.ContinueGamePopupLaunchGame);
    }
  }

  async onCloseClick(): Promise<void> {
    this.userDismissed.set(true);
    this.analyticsService.log(EventName.ContinueGamePopupDismiss);
    await safeModalDismiss(this.modalController);
  }
}
